import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, MoreHorizontalIcon, SettingsIcon, XIcon, PenIcon, PlusIcon, TrashIcon, ChevronDownIcon, CheckIcon, AlertTriangleIcon, ArchiveIcon, RefreshCcwIcon, CheckCircleIcon, XCircleIcon, UserPlusIcon, UploadIcon } from 'lucide-react';
import { mockBills, approveBill, rejectBill, updateBill } from '../mocks/mockBills';
type TabType = 'due' | 'pending' | 'scheduled' | 'paid' | 'assigned';
interface Threshold {
  id: string;
  approver: string;
  amount: number;
  currency: string;
}
// Cycle day options
const CYCLE_DAY_OPTIONS = [{
  value: 1,
  label: '1st of the month'
}, {
  value: 5,
  label: '5th of the month'
}, {
  value: 10,
  label: '10th of the month'
}, {
  value: 15,
  label: '15th of the month'
}, {
  value: 20,
  label: '20th of the month'
}, {
  value: 25,
  label: '25th of the month'
}, {
  value: -1,
  label: 'Last day of the month'
}];
export function Bills() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('due');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedBillIds, setSelectedBillIds] = useState<Set<string>>(new Set());
  // Force re-read of mutable mockBills on every render
  const [refreshKey, setRefreshKey] = useState(0);
  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);
  const [paymentCycles, setPaymentCycles] = useState<number[]>(() => {
    const stored = localStorage.getItem('billPaymentCycles');
    return stored ? JSON.parse(stored) : [];
  });
  const [numberOfApprovals, setNumberOfApprovals] = useState(0);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  // Modal states
  const [showApprovalsModal, setShowApprovalsModal] = useState(false);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [showCyclesModal, setShowCyclesModal] = useState(false);
  const [tempApprovals, setTempApprovals] = useState(0);
  const [tempThreshold, setTempThreshold] = useState({
    approver: 'Simon Verraest',
    amount: 0,
    currency: ''
  });
  const [tempCycles, setTempCycles] = useState<number[]>([]);
  // Action Modals State
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectAction, setRejectAction] = useState<'archive' | 'due'>('archive');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelAction, setCancelAction] = useState<'archive' | 'due'>('archive');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const [actionTargetId, setActionTargetId] = useState<string | null>(null);
  // Current user for approval logic
  const currentUser = 'Simon Verraest';
  // Whether the current tab supports multi-select
  const showCheckboxes = activeTab !== 'paid';
  // Check if a bill is selectable (for pending tab, only bills the user can approve)
  const isBillSelectable = (bill: (typeof mockBills)[0]) => {
    if (activeTab === 'paid') return false;
    if (activeTab === 'pending') {
      // Only bills assigned to current user or unassigned (open-ended by rules)
      return !bill.approver || bill.approver === currentUser;
    }
    return true;
  };
  // Filter bills based on active tab and search
  const filteredBills = useMemo(() => {
    return mockBills.filter(bill => {
      const matchesSearch = searchQuery === '' || bill.billNumber.toLowerCase().includes(searchQuery.toLowerCase()) || bill.supplierName.toLowerCase().includes(searchQuery.toLowerCase()) || bill.supplierEmail.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      switch (activeTab) {
        case 'due':
          return bill.status === 'approved' || bill.status === 'overdue';
        case 'pending':
          return bill.status === 'pending_approval' && (!bill.approver || bill.approver === currentUser);
        case 'scheduled':
          return bill.status === 'approved' && bill.approvalStatus === 'approved';
        case 'paid':
          return bill.status === 'paid';
        case 'assigned':
          return bill.approver === 'Simon Verraest' && bill.status === 'pending_approval';
        default:
          return true;
      }
    });
  }, [activeTab, searchQuery, refreshKey]);
  // Refresh data when component mounts (e.g. after navigating back from approval wizard)
  useEffect(() => {
    setRefreshKey(k => k + 1);
  }, []);
  const groupedBills = useMemo(() => {
    const groups: Record<string, typeof filteredBills> = {
      'Due next month': [],
      'Due this month': [],
      Older: []
    };
    filteredBills.forEach(bill => {
      if (bill.dueDate.includes('2026-04')) {
        groups['Due next month'].push(bill);
      } else if (bill.dueDate.includes('2026-03')) {
        groups['Due this month'].push(bill);
      } else {
        groups['Older'].push(bill);
      }
    });
    return Object.entries(groups).filter(([_, bills]) => bills.length > 0);
  }, [filteredBills]);
  const handleSelectAllInGroup = (groupBills: typeof filteredBills) => {
    const selectableBills = groupBills.filter(isBillSelectable);
    const allSelected = selectableBills.every(b => selectedBillIds.has(b.id));
    const newSelected = new Set(selectedBillIds);
    if (allSelected) {
      selectableBills.forEach(b => newSelected.delete(b.id));
    } else {
      selectableBills.forEach(b => newSelected.add(b.id));
    }
    setSelectedBillIds(newSelected);
  };
  const toggleBillSelection = (id: string) => {
    const bill = mockBills.find(b => b.id === id);
    if (bill && !isBillSelectable(bill)) return;
    const newSelected = new Set(selectedBillIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedBillIds(newSelected);
  };
  const selectedBills = mockBills.filter(b => selectedBillIds.has(b.id));
  const totalSelectedAmount = selectedBills.reduce((sum, b) => sum + b.amount, 0);
  const handleSchedulePayments = () => {
    if (selectedBillIds.size === 0) return;
    const idsParam = Array.from(selectedBillIds).join(',');
    navigate(`/bill-approval/schedule?ids=${idsParam}`);
  };
  const handleApproveBills = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowApproveModal(true);
    setContextMenuOpenId(null);
  };
  const confirmApproveBills = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => approveBill(id, currentUser));
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowApproveModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleRejectBills = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowRejectModal(true);
    setContextMenuOpenId(null);
  };
  const confirmRejectBills = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => {
      if (rejectAction === 'archive') {
        rejectBill(id, currentUser, 'Archived');
      } else {
        // Move back to due (approved status without schedule)
        updateBill(id, {
          status: 'approved',
          approvalStatus: 'approved',
          approver: undefined
        });
      }
    });
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowRejectModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleCancelScheduled = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowCancelModal(true);
    setContextMenuOpenId(null);
  };
  const confirmCancelScheduled = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => {
      if (cancelAction === 'archive') {
        rejectBill(id, currentUser, 'Archived');
      } else {
        // Move back to due
        updateBill(id, {
          status: 'approved',
          approvalStatus: 'approved',
          approver: undefined
        });
      }
    });
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowCancelModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleArchiveBill = (id: string) => {
    rejectBill(id, currentUser, 'Archived');
    setContextMenuOpenId(null);
    setRefreshKey(k => k + 1);
  };
  const handleReassignBill = (id: string) => {
    // Mock reassignment by clearing the approver so it goes back to general pending pool
    updateBill(id, {
      approver: undefined
    });
    setContextMenuOpenId(null);
    setRefreshKey(k => k + 1);
  };
  // Whether the current tab shows approve/reject actions
  const isApprovalTab = activeTab === 'assigned' || activeTab === 'pending';
  // Get summary label based on active tab
  const getSummaryLabel = () => {
    switch (activeTab) {
      case 'assigned':
      case 'pending':
        return 'Bills to review';
      case 'scheduled':
        return 'Scheduled payments';
      default:
        return 'Payments to schedule';
    }
  };
  const summaryLabel = getSummaryLabel();
  const handleSaveApprovals = () => {
    setNumberOfApprovals(tempApprovals);
    setShowApprovalsModal(false);
  };
  const handleSaveThreshold = () => {
    if (!tempThreshold.currency) return;
    setThresholds(prev => [...prev, {
      id: Date.now().toString(),
      approver: tempThreshold.approver,
      amount: tempThreshold.amount,
      currency: tempThreshold.currency
    }]);
    setTempThreshold({
      approver: 'Simon Verraest',
      amount: 0,
      currency: ''
    });
    setShowThresholdModal(false);
  };
  const removeThreshold = (id: string) => {
    setThresholds(prev => prev.filter(t => t.id !== id));
  };
  // Persist cycles to localStorage
  const handleSaveCycles = () => {
    localStorage.setItem('billPaymentCycles', JSON.stringify(tempCycles));
    setPaymentCycles(tempCycles);
    setShowCyclesModal(false);
  };
  const toggleCycleDay = (day: number) => {
    setTempCycles(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      }
      if (prev.length >= 2) return prev; // max 2
      return [...prev, day].sort((a, b) => a - b);
    });
  };
  const formatCycleLabel = (cycles: number[]) => {
    if (cycles.length === 0) return 'No payment cycles';
    return cycles.map(d => {
      const opt = CYCLE_DAY_OPTIONS.find(o => o.value === d);
      return opt ? opt.label : '';
    }).join(' & ');
  };
  return <div className="max-w-[1400px] mx-auto" data-id="element-4340">
      {/* Header */}
      <div className="flex justify-between items-center mb-8" data-id="element-4341">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4342">
          {t('pages.Bills.bills')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-4343">
          <button onClick={() => setShowSettings(true)} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-4344">
            <SettingsIcon size={18} data-id="element-4345" />
          </button>
          <button onClick={() => navigate('/bill-bulk-upload')} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-4346">
            <UploadIcon size={16} data-id="element-4347" />
            {t('pages.Bills.upload')}
          </button>
          <button onClick={() => navigate('/bill-form')} className="px-4 py-2 bg-[#0066b3] hover:bg-[#1a7dc4] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-4348">
            {t('pages.Bills.add_bill')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 items-start" data-id="element-4349">
        {/* Main Content */}
        <div className="xl:col-span-3 space-y-6" data-id="element-4350">
          {/* Tabs + Controls */}
          <div className="flex items-end justify-between border-b border-gray-200" data-id="element-4351">
            <div className="flex space-x-6" role="tablist" aria-label="Bills" data-id="element-4352">
              {[{
              id: 'due',
              label: 'Due'
            }, {
              id: 'pending',
              label: 'Pending approval'
            }, {
              id: 'scheduled',
              label: 'Scheduled'
            }, {
              id: 'paid',
              label: 'Paid'
            }, {
              id: 'assigned',
              label: 'Assigned to me'
            }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls={`bills-panel-${tab.id}`} id={`bills-tab-${tab.id}`} onClick={() => {
              setActiveTab(tab.id as TabType);
              setSelectedBillIds(new Set());
            }} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#0066b3] text-[#005291]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4353">
                  {tab.label}
                </button>)}
            </div>
            <div className="flex items-center gap-4 pb-3" data-id="element-4354">
              <div className="relative" data-id="element-4355">
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-4356" />

                <input type="search" aria-label={t('pages.Bills.attrs.search_bills')} placeholder={t('pages.Bills.attrs.search_bills')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0066b3]/20 focus:border-[#0066b3] transition-colors" data-id="element-4357" />
              </div>
              <label className="flex items-center cursor-pointer" data-id="element-4358">
                <div className="relative" data-id="element-4359">
                  <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-4360" />

                  <div className={`block w-9 h-5 rounded-full transition-colors ${showArchived ? 'bg-[#0066b3]' : 'bg-gray-200'}`} data-id="element-4361" />

                  <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-4362" />
                </div>
                <span className="text-xs text-gray-600 ml-2 font-medium" data-id="element-4363">
                  {t('pages.Bills.show_archived')}
                </span>
              </label>
            </div>
          </div>

          {/* Bills List */}
          <div className="space-y-8" data-id="element-4364">
            {groupedBills.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-4365">
                <p className="text-gray-500 text-sm" data-id="element-4366">
                  {t('pages.Bills.no_bills_found_matching_your_criteria')}
                </p>
              </div> : groupedBills.map(([groupName, groupBills]) => <div key={groupName} className="space-y-3" data-id="element-4367">
                  <div className="flex items-center gap-3" data-id="element-4368">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-4369">
                      {groupName}
                    </h3>
                    {showCheckboxes && <button onClick={() => handleSelectAllInGroup(groupBills)} className="text-xs font-medium text-[#005291] hover:text-[#004f87]" data-id="element-4370">
                        {t('pages.Bills.select')}
                      </button>}
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-4371">
                    <table className="w-full text-left border-collapse" data-id="element-4372">
                      <tbody className="divide-y divide-gray-100" data-id="element-4373">
                        {groupBills.map(bill => {
                    const selectable = isBillSelectable(bill);
                    return <tr key={bill.id} className={`hover:bg-gray-50 transition-colors ${selectedBillIds.has(bill.id) ? 'bg-yellow-50/50' : ''}`} data-id="element-4374">
                              {showCheckboxes && <td className="px-4 py-4 w-12" data-id="element-4375">
                                  {selectable ? <input type="checkbox" checked={selectedBillIds.has(bill.id)} onChange={() => toggleBillSelection(bill.id)} className="w-4 h-4 text-[#0066b3] border-gray-300 rounded focus:ring-[#0066b3] cursor-pointer" data-id="element-4376" /> : <input type="checkbox" disabled className="w-4 h-4 border-gray-200 rounded opacity-30 cursor-not-allowed" data-id="element-4377" />}
                                </td>}
                              <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4378">
                                <div className="text-sm font-semibold text-gray-900" data-id="element-4379">
                                  {bill.supplierName}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5" data-id="element-4380">
                                  {bill.billNumber}
                                </div>
                              </td>
                              <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4381">
                                <div className="text-sm font-medium text-gray-900" data-id="element-4382">
                                  {t('pages.BillApprovalWizard.usd')}{' '}
                                  {bill.amount.toLocaleString('en-US', {
                            minimumFractionDigits: 2
                          })}
                                </div>
                              </td>
                              {activeTab === 'paid' ? <>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4383">
                                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium" data-id="element-4384">
                                      {t('pages.Bills.source')}
                                    </div>
                                    <div className="text-sm text-gray-900 mt-0.5" data-id="element-4385">
                                      {bill.paymentSource === 'balance' ? 'Balance' : bill.paymentSource === 'card' ? 'Card' : bill.paymentSource === 'bank_transfer' ? 'Bank Transfer' : '—'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4386">
                                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium" data-id="element-4387">
                                      {t('pages.Bills.payout')}
                                    </div>
                                    <div className="text-sm text-gray-900 mt-0.5" data-id="element-4388">
                                      {bill.payoutType === 'bank_account' ? 'Bank Account' : bill.payoutType === 'promptpay' ? 'PromptPay' : '—'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4389">
                                    <div className="text-sm text-gray-500" data-id="element-4390">
                                      {t('pages.BillDetail.paid_on')}{' '}
                                      {bill.paidDate ? new Date(bill.paidDate).toLocaleDateString('en-GB') : '—'}
                                    </div>
                                  </td>
                                </> : <>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4391">
                                    <div className="flex flex-col items-start gap-1" data-id="element-4392">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4393">
                                        {bill.status === 'pending_approval' ? 'Pending' : bill.status === 'approved' ? 'Due' : bill.status.charAt(0).toUpperCase() + bill.status.slice(1)}
                                      </span>
                                      {bill.status === 'pending_approval' && numberOfApprovals > 1 && <span className="text-[10px] text-gray-500 font-medium bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100" data-id="element-4394">
                                            {t('pages.Bills.requires')}{' '}
                                            {numberOfApprovals}{' '}
                                            {t('pages.Bills.approvals')}
                                          </span>}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4395">
                                    <div className="text-sm text-gray-500" data-id="element-4396">
                                      {t('pages.Bills.due_on')}{' '}
                                      {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                                    </div>
                                  </td>
                                </>}
                              <td className="px-4 py-4 text-right w-12 relative" data-id="element-4397">
                                {activeTab !== 'paid' && <>
                                    <button onClick={e => {
                            e.stopPropagation();
                            setContextMenuOpenId(contextMenuOpenId === bill.id ? null : bill.id);
                          }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" aria-label="Bill actions" data-id="element-4398">
                                      <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-4399" />
                                    </button>

                                    {contextMenuOpenId === bill.id && <>
                                        <div className="fixed inset-0 z-10" onClick={e => {
                              e.stopPropagation();
                              setContextMenuOpenId(null);
                            }} data-id="element-4400" />

                                        <div className="absolute right-8 top-10 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 animate-in fade-in zoom-in-95 duration-100" data-id="element-4401">
                                          {activeTab === 'due' && <button onClick={e => {
                                e.stopPropagation();
                                handleArchiveBill(bill.id);
                              }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4402">
                                              <ArchiveIcon size={14} className="mr-2" data-id="element-4403" />

                                              {t('pages.Bills.archive_bill')}
                                            </button>}

                                          {activeTab === 'pending' && <>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleApproveBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4404">
                                                <CheckCircleIcon size={14} className="mr-2 text-green-600" data-id="element-4405" />

                                                {t('pages.Bills.approve')}
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleRejectBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4406">
                                                <XCircleIcon size={14} className="mr-2" data-id="element-4407" />

                                                {t('pages.BillApprovalWizard.cancel')}
                                              </button>
                                            </>}

                                          {activeTab === 'scheduled' && <button onClick={e => {
                                e.stopPropagation();
                                handleCancelScheduled(bill.id);
                              }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4408">
                                              <XCircleIcon size={14} className="mr-2" data-id="element-4409" />

                                              {t('pages.Bills.cancel_payment')}
                                            </button>}

                                          {activeTab === 'assigned' && <>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleApproveBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4410">
                                                <CheckCircleIcon size={14} className="mr-2 text-green-600" data-id="element-4411" />

                                                {t('pages.Bills.approve')}
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleReassignBill(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4412">
                                                <UserPlusIcon size={14} className="mr-2 text-blue-600" data-id="element-4413" />

                                                {t('pages.Bills.reassign')}
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleRejectBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4414">
                                                <XCircleIcon size={14} className="mr-2" data-id="element-4415" />

                                                {t('pages.BillApprovalWizard.cancel')}
                                              </button>
                                            </>}
                                        </div>
                                      </>}
                                  </>}
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                </div>)}
          </div>
        </div>

        {/* Right Sidebar (Summary) */}
        <div className="xl:col-span-1" data-id="element-4416">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 sticky top-6" data-id="element-4417">
            <div className="flex justify-between items-start mb-6" data-id="element-4418">
              <div data-id="element-4419">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-4420">
                  {t('pages.BillApprovalWizard.summary')}
                </h2>
                <p className="text-xs text-gray-500 mt-1" data-id="element-4421">
                  {t('pages.Bills.based_on_your_selected_bills')}
                </p>
              </div>
              {selectedBillIds.size > 0 && <button onClick={() => setSelectedBillIds(new Set())} className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors" data-id="element-4422">
                  {t('pages.Bills.clear')}
                </button>}
            </div>
            <div className="space-y-4 mb-8" data-id="element-4423">
              <div className="flex justify-between items-center text-sm" data-id="element-4424">
                <span className="text-gray-600" data-id="element-4425">
                  {summaryLabel} - {selectedBillIds.size}
                </span>
                {selectedBillIds.size === 0 && <span className="text-gray-400 text-xs" data-id="element-4426">
                    {t('pages.Bills.no_selected_bills')}
                  </span>}
              </div>
              {selectedBillIds.size > 0 && <div className="flex justify-between items-center pt-4 border-t border-gray-200" data-id="element-4427">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-4428">
                    {t('pages.BillDetail.total')}
                  </span>
                  <span className="text-sm font-bold text-gray-900" data-id="element-4429">
                    {t('pages.BillApprovalWizard.usd')}{' '}
                    {totalSelectedAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                  </span>
                </div>}
            </div>

            {isApprovalTab ? <div className="flex gap-2" data-id="element-4430">
                <button onClick={() => handleRejectBills()} disabled={selectedBillIds.size === 0} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-white border border-red-300 text-red-600 hover:bg-red-50 shadow-sm' : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'}`} data-id="element-4431">
                  {t('pages.Bills.reject')}
                </button>
                <button onClick={() => handleApproveBills()} disabled={selectedBillIds.size === 0} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-[#0066b3] hover:bg-[#1a7dc4] text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4432">
                  {t('pages.Bills.approve')}
                </button>
              </div> : activeTab === 'scheduled' ? <button onClick={() => handleCancelScheduled()} disabled={selectedBillIds.size === 0} className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-red-600 hover:bg-red-700 text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4433">
                {t('pages.Bills.cancel_scheduled_payments')}
              </button> : <button onClick={handleSchedulePayments} disabled={selectedBillIds.size === 0} className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-[#0066b3] hover:bg-[#1a7dc4] text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4434">
                {t('pages.Bills.schedule_payments')}
              </button>}
          </div>
        </div>
      </div>

      {/* ========== ACTION MODALS ========== */}

      {/* Reject Modal */}
      {showRejectModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowRejectModal(false)} data-id="element-4435" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4436">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4437">
              <div className="p-6" data-id="element-4438">
                <div className="flex items-center gap-3 mb-4" data-id="element-4439">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-4440">
                    <AlertTriangleIcon size={20} className="text-red-600" data-id="element-4441" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-4442">
                    {t('pages.Bills.reject')}{' '}
                    {actionTargetId ? 'bill' : `${selectedBillIds.size} bills`}
                  </h2>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-4443">
                  {t('pages.Bills.what_would_you_like_to_do')}{' '}
                  {actionTargetId ? 'bill' : 'bills'}?
                </p>

                <div className="space-y-3 mb-8" data-id="element-4444">
                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${rejectAction === 'archive' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4445">
                    <input type="radio" name="rejectAction" checked={rejectAction === 'archive'} onChange={() => setRejectAction('archive')} className="mt-0.5 text-red-600 focus:ring-red-500" data-id="element-4446" />

                    <div className="ml-3" data-id="element-4447">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4448">
                        {t('pages.Bills.archive_bills')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4449">
                        {t('pages.Bills.remove_them_from_the_active_list')}
                      </span>
                    </div>
                  </label>

                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${rejectAction === 'due' ? 'border-[#0066b3] bg-[#E6F1F9]' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4450">
                    <input type="radio" name="rejectAction" checked={rejectAction === 'due'} onChange={() => setRejectAction('due')} className="mt-0.5 text-[#0066b3] focus:ring-[#0066b3]" data-id="element-4451" />

                    <div className="ml-3" data-id="element-4452">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4453">
                        {t('pages.Bills.move_back_to_due')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4454">
                        {t('pages.Bills.return_them_to_the_due_list')}
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end gap-3" data-id="element-4455">
                  <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4456">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={confirmRejectBills} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors" data-id="element-4457">
                    {t('pages.Bills.confirm_rejection')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Approve Modal */}
      {showApproveModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowApproveModal(false)} data-id="element-4458" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4459">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4460">
              <div className="p-6 text-center" data-id="element-4461">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-4462">
                  <CheckCircleIcon size={24} className="text-green-600" data-id="element-4463" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-4464">
                  {t('pages.Bills.confirm_approval')}
                </h2>
                <p className="text-sm text-gray-600 mb-8" data-id="element-4465">
                  {t('pages.Bills.are_you_sure_you_want_to')}{' '}
                  {actionTargetId ? 'this bill' : `these ${selectedBillIds.size} bills`}
                  ?
                  {numberOfApprovals > 1 && ' This will count as 1 of the required approvals.'}
                </p>
                <div className="flex justify-center gap-3" data-id="element-4466">
                  <button onClick={() => setShowApproveModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4467">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={confirmApproveBills} className="px-4 py-2 text-sm font-medium text-white bg-[#0066b3] rounded-lg hover:bg-[#1a7dc4] transition-colors" data-id="element-4468">
                    {t('pages.Bills.confirm_approval')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Cancel Scheduled Modal */}
      {showCancelModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowCancelModal(false)} data-id="element-4469" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4470">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4471">
              <div className="p-6" data-id="element-4472">
                <div className="flex items-center gap-3 mb-4" data-id="element-4473">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-4474">
                    <AlertTriangleIcon size={20} className="text-red-600" data-id="element-4475" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-4476">
                    {t('pages.Bills.cancel_scheduled')}{' '}
                    {actionTargetId ? 'Payment' : 'Payments'}
                  </h2>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-4477">
                  {t('pages.Bills.what_would_you_like_to_do_2')}{' '}
                  {actionTargetId ? 'payment' : 'payments'}?
                </p>

                <div className="space-y-3 mb-8" data-id="element-4478">
                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${cancelAction === 'due' ? 'border-[#0066b3] bg-[#E6F1F9]' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4479">
                    <input type="radio" name="cancelAction" checked={cancelAction === 'due'} onChange={() => setCancelAction('due')} className="mt-0.5 text-[#0066b3] focus:ring-[#0066b3]" data-id="element-4480" />

                    <div className="ml-3" data-id="element-4481">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4482">
                        {t('pages.Bills.move_back_to_due')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4483">
                        {t('pages.Bills.return_them_to_the_due_list_2')}
                      </span>
                    </div>
                  </label>

                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${cancelAction === 'archive' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4484">
                    <input type="radio" name="cancelAction" checked={cancelAction === 'archive'} onChange={() => setCancelAction('archive')} className="mt-0.5 text-red-600 focus:ring-red-500" data-id="element-4485" />

                    <div className="ml-3" data-id="element-4486">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4487">
                        {t('pages.Bills.archive_bills')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4488">
                        {t('pages.Bills.remove_them_from_the_active_list')}
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end gap-3" data-id="element-4489">
                  <button onClick={() => setShowCancelModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4490">
                    {t('pages.Bills.keep_scheduled')}
                  </button>
                  <button onClick={confirmCancelScheduled} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors" data-id="element-4491">
                    {t('pages.Bills.confirm_cancellation')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== BILLS SETTINGS SLIDE-OUT PANEL ========== */}
      {showSettings && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowSettings(false)} data-id="element-4492" />

          {/* Panel */}
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200" data-id="element-4493">
            <div className="p-8" data-id="element-4494">
              {/* Close button */}
              <div className="flex justify-end mb-6" data-id="element-4495">
                <button onClick={() => setShowSettings(false)} aria-label="Close settings" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4496">
                  <XIcon size={18} aria-hidden="true" data-id="element-4497" />
                </button>
              </div>

              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-4498">
                {t('pages.Bills.bills_settings')}
              </h2>

              {/* Bill payments can be approved by */}
              <div className="mb-8" data-id="element-4499">
                <h3 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-4500">
                  {t('pages.Bills.bill_payments_can_be_approved_by')}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed" data-id="element-4501">
                  {t('pages.Bills.users_in_these_user_groups_can')}
                </p>
              </div>

              {/* Billing Cycles */}
              <div className="mb-8" data-id="element-4502">
                <h3 className="text-xs font-medium text-gray-500 mb-2" data-id="element-4503">
                  {t('pages.Bills.billing_cycles')}
                </h3>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4504">
                  <span className="text-sm font-medium text-gray-900" data-id="element-4505">
                    {formatCycleLabel(paymentCycles)}
                  </span>
                  <button onClick={() => {
                setTempCycles([...paymentCycles]);
                setShowCyclesModal(true);
              }} aria-label={t('pages.Bills.edit_payment_cycles', {
                defaultValue: 'Edit payment cycles'
              })} className="text-[#005291] hover:text-[#004f87] transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-4506">
                    <PenIcon size={16} aria-hidden="true" data-id="element-4507" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed" data-id="element-4508">
                  {t('pages.Bills.automate_your_payment_runs_by_setting')}
                </p>
              </div>

              {/* Number of Approvals */}
              <div className="mb-8" data-id="element-4509">
                <h3 className="text-xs font-medium text-gray-500 mb-2" data-id="element-4510">
                  {t('pages.Bills.number_of_approvals')}
                </h3>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4511">
                  <span className="text-sm font-medium text-gray-900" data-id="element-4512">
                    {numberOfApprovals}
                  </span>
                  <button onClick={() => {
                setTempApprovals(numberOfApprovals);
                setShowApprovalsModal(true);
              }} aria-label={t('pages.Bills.edit_number_of_approvals', {
                defaultValue: 'Edit number of approvals'
              })} className="text-[#005291] hover:text-[#004f87] transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-4513">
                    <PenIcon size={16} aria-hidden="true" data-id="element-4514" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed" data-id="element-4515">
                  {t('pages.Bills.approving_any_payment_for_bills_will')}{' '}
                  {numberOfApprovals}{' '}
                  {t('pages.Bills.users_to_approve_except_when_threshold')}
                </p>
              </div>

              {/* Limits and Thresholds */}
              <div data-id="element-4516">
                <div className="flex items-center justify-between mb-2" data-id="element-4517">
                  <h3 className="text-xs font-medium text-gray-500" data-id="element-4518">
                    {t('pages.Bills.limits_and_thresholds')}
                  </h3>
                  <button onClick={() => {
                setTempThreshold({
                  approver: 'Simon Verraest',
                  amount: 0,
                  currency: ''
                });
                setShowThresholdModal(true);
              }} className="text-xs font-medium text-[#005291] hover:text-[#004f87] transition-colors" data-id="element-4519">
                    {t('pages.Bills.add_threshold')}
                  </button>
                </div>

                {thresholds.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4520">
                    <span className="text-sm text-gray-500" data-id="element-4521">
                      {t('pages.Bills.no_specific_threshold_approvers_configured_yet')}
                    </span>
                  </div> : <div className="space-y-2" data-id="element-4522">
                    {thresholds.map(thr => <div key={thr.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4523">
                        <div data-id="element-4524">
                          <div className="text-sm font-medium text-gray-900" data-id="element-4525">
                            {thr.approver}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-4526">
                            {t('pages.Bills.bills_above')} {thr.currency}{' '}
                            {thr.amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2
                    })}
                          </div>
                        </div>
                        <button onClick={() => removeThreshold(thr.id)} className="text-[#005291] hover:text-[#004f87] transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={`Remove threshold for ${thr.approver}`} data-id="element-4527">
                          <TrashIcon size={14} data-id="element-4528" />
                        </button>
                      </div>)}
                  </div>}

                <p className="text-xs text-gray-500 mt-3 leading-relaxed" data-id="element-4529">
                  {t('pages.Bills.when_threshold_rules_are_configured_payments')}
                </p>
              </div>
            </div>
          </div>
        </>}

      {/* ========== NUMBER OF APPROVALS MODAL ========== */}
      {showApprovalsModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowApprovalsModal(false)} data-id="element-4530" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4531">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-4532">
              <div className="p-8" data-id="element-4533">
                <div className="flex justify-end mb-4" data-id="element-4534">
                  <button onClick={() => setShowApprovalsModal(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4535">
                    <XIcon size={18} aria-hidden="true" data-id="element-4536" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-4537">
                  {t('pages.Bills.number_of_approvals')}
                </h2>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed" data-id="element-4538">
                  {t('pages.Bills.the_default_number_of_staff_members')}
                </p>

                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4539">
                  {t('pages.Bills.number_of_approvals')}
                </label>
                <input type="number" min="0" value={tempApprovals} onChange={e => setTempApprovals(parseInt(e.target.value) || 0)} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0066b3]/20 focus:border-[#0066b3] transition-colors" data-id="element-4540" />

                <div className="flex justify-end gap-3 mt-8" data-id="element-4541">
                  <button onClick={() => setShowApprovalsModal(false)} className="px-6 py-2.5 text-sm font-medium text-[#005291] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4542">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={handleSaveApprovals} className="px-6 py-2.5 text-sm font-medium text-white bg-gray-400 rounded-lg hover:bg-gray-500 transition-colors" data-id="element-4543">
                    {t('pages.BillForm.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== ADD THRESHOLD MODAL ========== */}
      {showThresholdModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowThresholdModal(false)} data-id="element-4544" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4545">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-4546">
              <div className="p-8" data-id="element-4547">
                <div className="flex justify-end mb-4" data-id="element-4548">
                  <button onClick={() => setShowThresholdModal(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4549">
                    <XIcon size={18} aria-hidden="true" data-id="element-4550" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-4551">
                  {t('pages.Bills.add_threshold')}
                </h2>

                <div className="space-y-5" data-id="element-4552">
                  {/* Approver */}
                  <div data-id="element-4553">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4554">
                      {t('pages.Bills.approver_name')}
                    </label>
                    <input type="text" value={tempThreshold.approver} onChange={e => setTempThreshold({
                  ...tempThreshold,
                  approver: e.target.value
                })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0066b3]/20 focus:border-[#0066b3] transition-colors" data-id="element-4555" />
                  </div>

                  {/* Amount */}
                  <div data-id="element-4556">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4557">
                      {t('pages.Bills.this_user_will_need_to_approve')}
                    </label>
                    <input type="number" min="0" value={tempThreshold.amount} onChange={e => setTempThreshold({
                  ...tempThreshold,
                  amount: parseFloat(e.target.value) || 0
                })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0066b3]/20 focus:border-[#0066b3] transition-colors" data-id="element-4558" />
                  </div>

                  {/* Currency */}
                  <div data-id="element-4559">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4560">
                      {t('pages.Bills.select_a_currency_for_this_threshold')}
                    </label>
                    <div className="relative" data-id="element-4561">
                      <select value={tempThreshold.currency} onChange={e => setTempThreshold({
                    ...tempThreshold,
                    currency: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0066b3]/20 focus:border-[#0066b3] transition-colors" data-id="element-4562">
                        <option value="" disabled data-id="element-4563">
                          {t('pages.Bills.select_a_currency')}
                        </option>
                        <option value="MYR" data-id="element-4564">{t('pages.Bills.myr')}</option>
                        <option value="MYR" data-id="element-4565">MYR</option>
                        <option value="EUR" data-id="element-4566">{t('pages.Bills.eur')}</option>
                        <option value="GBP" data-id="element-4567">{t('pages.Bills.gbp')}</option>
                        <option value="SGD" data-id="element-4568">{t('pages.Bills.sgd')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-4569">
                  <button onClick={() => setShowThresholdModal(false)} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4570">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={handleSaveThreshold} disabled={!tempThreshold.currency} className="px-6 py-2.5 text-sm font-medium text-[#005291] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4571">
                    {t('pages.Bills.add')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== ADD CYCLES MODAL ========== */}
      {showCyclesModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowCyclesModal(false)} data-id="element-4572" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4573">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-4574">
              <div className="p-8" data-id="element-4575">
                <div className="flex justify-end mb-4" data-id="element-4576">
                  <button onClick={() => setShowCyclesModal(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4577">
                    <XIcon size={18} aria-hidden="true" data-id="element-4578" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-4579">
                  {t('pages.Bills.payment_cycles')}
                </h2>

                <div className="space-y-4" data-id="element-4580">
                  {CYCLE_DAY_OPTIONS.map(opt => <label key={opt.value} className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-4581">
                      <input type="checkbox" checked={tempCycles.includes(opt.value)} onChange={() => toggleCycleDay(opt.value)} disabled={!tempCycles.includes(opt.value) && tempCycles.length >= 2} className="w-4 h-4 text-[#0066b3] border-gray-300 rounded focus:ring-[#0066b3] cursor-pointer" data-id="element-4582" />

                      <span className="ml-3 text-sm text-gray-700" data-id="element-4583">
                        {opt.label}
                      </span>
                    </label>)}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-4584">
                  <button onClick={() => setShowCyclesModal(false)} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4585">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={handleSaveCycles} className="px-6 py-2.5 text-sm font-medium text-[#005291] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4586">
                    {t('pages.Bills.save_cycles')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}